import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import SearchableSelect from '@/components/common/SearchableSelect';
import { ROLES } from '@/types/auth';
import type { UserDto } from '@/types/auth';
import type { DepartmentDto, RoleDto, UserAccessScope } from '@/types/administration';
import type { ClientDto } from '@/types/materials';
import type { BranchDto, WarehouseDto } from '@/types/organization';

function buildSchema(t: (key: string) => string, isEdit: boolean) {
  return z.object({
    username: isEdit ? z.string() : z.string().min(1, t('form.validation.usernameRequired')),
    email: z.string().email(t('form.validation.emailRequired')),
    password: isEdit ? z.string().optional() : z.string().min(8, t('form.validation.passwordMinLength')),
    firstName: z.string().min(1, t('form.validation.firstNameRequired')),
    lastName: z.string().min(1, t('form.validation.lastNameRequired')),
    departmentId: z.string().nullable(),
    accessScope: z.enum(['Company', 'Branch', 'Warehouse']),
    branchId: z.string().nullable(),
    defaultWarehouseId: z.string().nullable(),
    roleIds: z.array(z.string()).min(1, t('form.validation.rolesRequired')),
    isActive: z.boolean(),
    clientId: z.string().nullable(),
  })
    // Mirrors UserService.ResolveAccessScopeAsync on the server — a narrowed scope is meaningless
    // without the branch/warehouse it narrows to, so it's caught here rather than round-tripping.
    .refine((v) => v.accessScope === 'Company' || Boolean(v.branchId), {
      path: ['branchId'], message: t('form.validation.branchRequired'),
    })
    .refine((v) => v.accessScope !== 'Warehouse' || Boolean(v.defaultWarehouseId), {
      path: ['defaultWarehouseId'], message: t('form.validation.warehouseRequired'),
    });
}

export type UserFormValues = z.infer<ReturnType<typeof buildSchema>>;

interface UserFormDialogProps {
  open: boolean;
  user: UserDto | null;
  onClose: () => void;
  onSubmit: (values: UserFormValues) => void;
  loading?: boolean;
}

export default function UserFormDialog({ open, user, onClose, onSubmit, loading }: UserFormDialogProps) {
  const { t } = useTranslation('userManagement');
  const isEdit = Boolean(user);
  const schema = buildSchema(t, isEdit);

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => (await apiClient.get<DepartmentDto[]>('/departments')).data,
    enabled: open,
  });
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await apiClient.get<RoleDto[]>('/roles')).data,
    enabled: open,
  });
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
    enabled: open,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => (await apiClient.get<BranchDto[]>('/branches')).data,
    enabled: open,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
    enabled: open,
  });

  const emptyValues: UserFormValues = {
    username: '', email: '', password: '', firstName: '', lastName: '', departmentId: null,
    accessScope: 'Company', branchId: null, defaultWarehouseId: null,
    roleIds: [], isActive: true, clientId: null,
  };

  const { control, handleSubmit, reset, watch, setValue } = useForm<UserFormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  const selectedRoleIds = watch('roleIds');
  const accessScope = watch('accessScope');
  const selectedBranchId = watch('branchId');

  // A warehouse is only ever pickable from within the chosen branch — the server enforces the same
  // rule, and offering the rest would just be an error waiting to happen.
  const branchWarehouses = warehouses.filter((w) => !selectedBranchId || w.branchId === selectedBranchId);
  const clientPortalRoleId = roles.find((r) => r.name === ROLES.CLIENT_PORTAL)?.id;
  const isClientPortalSelected = Boolean(clientPortalRoleId && selectedRoleIds.includes(clientPortalRoleId));

  useEffect(() => {
    if (open) {
      reset(
        user
          ? {
              ...emptyValues,
              username: user.username, email: user.email, password: '', firstName: user.firstName, lastName: user.lastName,
              accessScope: user.accessScope ?? 'Company', branchId: user.branchId, defaultWarehouseId: user.defaultWarehouseId,
              isActive: user.isActive, clientId: user.clientId,
            }
          : emptyValues,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{user ? user.fullName : t('addUser')}</DialogTitle>
      <DialogContent>
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={1}>
            <Controller
              name="username"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.username')} fullWidth sx={{ mt: 1 }} disabled={isEdit} error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.email')} fullWidth sx={{ mt: 1 }} error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          {!isEdit && (
            <GridItem xs={2}>
              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField {...field} type="password" label={t('form.password')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
                )}
              />
            </GridItem>
          )}
          <GridItem xs={1}>
            <Controller
              name="firstName"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.firstName')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="lastName"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.lastName')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={2}>
            <Controller
              name="departmentId"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  label={t('form.department')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                  options={[{ value: '', label: '—' }, ...departments.map((d) => ({ value: d.id, label: d.name }))]}
                />
              )}
            />
          </GridItem>

          {/* Access scope — how much of the company this user can see and act on. Company covers
              every branch and warehouse; Branch covers one branch and every warehouse under it;
              Warehouse covers a single warehouse. Enforced server-side by narrowing the Branch and
              Warehouse query filters, which every transaction in the system filters through. */}
          <GridItem xs={2}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {t('form.accessScope')}
            </Typography>
            <Controller
              name="accessScope"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  row
                  value={field.value}
                  onChange={(e) => {
                    const next = e.target.value as UserAccessScope;
                    field.onChange(next);
                    // Clear what the new scope no longer uses, so a stale id can't be submitted.
                    if (next === 'Company') {
                      setValue('branchId', null);
                      setValue('defaultWarehouseId', null);
                    } else if (next === 'Branch') {
                      setValue('defaultWarehouseId', null);
                    }
                  }}
                >
                  <FormControlLabel value="Company" control={<Radio />} label={t('form.accessScopes.Company')} />
                  <FormControlLabel value="Branch" control={<Radio />} label={t('form.accessScopes.Branch')} />
                  <FormControlLabel value="Warehouse" control={<Radio />} label={t('form.accessScopes.Warehouse')} />
                </RadioGroup>
              )}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t(`form.accessScopeHints.${accessScope}`)}
            </Typography>
          </GridItem>

          {accessScope !== 'Company' && (
            <GridItem xs={accessScope === 'Warehouse' ? 1 : 2}>
              <Controller
                name="branchId"
                control={control}
                render={({ field, fieldState }) => (
                  <SearchableSelect
                    label={t('form.branch')} value={field.value ?? ''} fullWidth
                    onChange={(next) => {
                      field.onChange(next);
                      // The warehouse must live inside the chosen branch — drop it when the branch
                      // moves out from under it rather than submitting a mismatched pair.
                      setValue('defaultWarehouseId', null);
                    }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    options={branches.map((b) => ({ value: b.id, label: b.name }))}
                  />
                )}
              />
            </GridItem>
          )}

          {accessScope === 'Warehouse' && (
            <GridItem xs={1}>
              <Controller
                name="defaultWarehouseId"
                control={control}
                render={({ field, fieldState }) => (
                  <SearchableSelect
                    label={t('form.warehouse')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message ?? (selectedBranchId ? undefined : t('form.selectBranchFirst'))}
                    options={branchWarehouses.map((w) => ({ value: w.id, label: w.name }))}
                  />
                )}
              />
            </GridItem>
          )}

          <GridItem xs={2}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {t('form.roles')}
            </Typography>
            <Controller
              name="roleIds"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormGroup row>
                    {roles.map((r) => (
                      <FormControlLabel
                        key={r.id}
                        control={
                          <Checkbox
                            checked={field.value.includes(r.id)}
                            onChange={(e) =>
                              field.onChange(e.target.checked ? [...field.value, r.id] : field.value.filter((id) => id !== r.id))
                            }
                          />
                        }
                        label={r.name}
                      />
                    ))}
                  </FormGroup>
                  {fieldState.error ? (
                    <Typography variant="caption" color="error">
                      {fieldState.error.message}
                    </Typography>
                  ) : undefined}
                </>
              )}
            />
          </GridItem>
          {isClientPortalSelected && (
            <GridItem xs={2}>
              <Controller
                name="clientId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    label={t('form.client')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                    options={clients.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                  />
                )}
              />
            </GridItem>
          )}
          {isEdit && (
            <GridItem xs={2}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label={t('form.isActive')} />
                )}
              />
            </GridItem>
          )}
        </GridContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
